"use client";
import React, { useContext, useEffect, useMemo, useState } from "react";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import {
  ResponsiveDrawerDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownHeader,
} from "@stadium/ui/src/components/bootstrap-dropdown";
import { Spinner } from "@stadium/ui/src/components/loaders/loaders";

import { WeglotContext } from "@stadium/services/weglot/provider";
import {
  getSelectedLanguage,
  isForceTranslationsEnabled,
  setWeglotLangCookie,
} from "@stadium/services/weglot/helpers";
import { getCountryCodeAndLangFromPathname } from "@stadium/helpers/store";

import { DEFAULT_LANG_OBJ } from "@stadium/services/weglot/constants";
import { LANGUAGE_TRANSLATOR_DROPDOWN_ID } from "@stadium/ui/src/constants/weglot-translator";

import defaultStyles from "./main.module.scss";

const defaultObj = {};

const WeglotDropDown = ({ styles = defaultStyles, onLanguageSelection }) => {
  const router = useRouter();
  const {
    query: { lang: currentLang, countryCode: localeCode },
  } = router;

  const lang = useMemo(
    () =>
      localeCode?.includes("-") ? getCountryCodeAndLangFromPathname(localeCode).lang : currentLang,
    [currentLang, localeCode],
  );

  const { instance, availableLanguages, switchLanguage } = useContext(WeglotContext);
  const [selectedLang, setSelectedLang] = useState(DEFAULT_LANG_OBJ.key);

  const applyLanguageToWeglot = (languageToBeSelected) => {
    setWeglotLangCookie(languageToBeSelected);
    switchLanguage(languageToBeSelected);
    setSelectedLang(languageToBeSelected);
  };

  const handleUserLanguageSelection = (languageToBeSelected) => {
    if (onLanguageSelection) {
      onLanguageSelection(languageToBeSelected);
    }
    applyLanguageToWeglot(languageToBeSelected);
  };
  const forceTranslations = isForceTranslationsEnabled(router.query);
  const isLoadingLanguages = !availableLanguages?.length;

  useEffect(() => {
    if (!availableLanguages.length) return;

    const languageToBeSelected = getSelectedLanguage({
      weglotLanguages: availableLanguages,
      langInUrl: lang,
      weglotInstance: instance,
      forceTranslations,
    });
    applyLanguageToWeglot(languageToBeSelected);
  }, [availableLanguages, instance, forceTranslations, lang, switchLanguage, onLanguageSelection]);

  const dropdownOptions = useMemo(
    () =>
      availableLanguages.map(({ key, label }) => ({
        value: key,
        text: label,
      })),
    [availableLanguages],
  );

  const renderOptions = useMemo(
    () =>
      dropdownOptions.map(({ text, value, id }) => (
        <DropdownItem
          isActive={selectedLang === value}
          key={id}
          eventKey={id}
          onSelect={() => {
            if (selectedLang !== value) {
              handleUserLanguageSelection(value);
            }
          }}
          styles={styles}
        >
          {text}
        </DropdownItem>
      )),
    [dropdownOptions, selectedLang],
  );

  const renderLabel = () => {
    const { options, getLanguageName } = instance || defaultObj;
    if (options?.languages) {
      return (
        <DropdownToggle styles={styles} headingStyle="uppercase" styleType="no-border">
          {getLanguageName?.(selectedLang)}
        </DropdownToggle>
      );
    }
    return (
      <DropdownToggle styles={styles} headingStyle="uppercase" styleType="no-border">
        {DEFAULT_LANG_OBJ.label}
      </DropdownToggle>
    );
  };

  return (
    <ResponsiveDrawerDropdown
      styles={styles}
      heading={<DropdownToggle>Select Language</DropdownToggle>}
      id={LANGUAGE_TRANSLATOR_DROPDOWN_ID}
      label={renderLabel()}
      className={`weglot-dropdown ${styles.weglotDropdown}`}
      menuClassName={styles.weglotMenuDropdown}
    >
      {isLoadingLanguages ? (
        <li className={styles.languageLoader}>
          <div className={styles.languageLoaderSpinner}>
            <Spinner />
          </div>
          <div className={styles.languageLoaderText}>Loading Languages</div>
        </li>
      ) : (
        <>
          <DropdownHeader className={styles.languageDropdownHeaderText}>
            Translate Page To
          </DropdownHeader>
          {renderOptions}
        </>
      )}
    </ResponsiveDrawerDropdown>
  );
};

export default WeglotDropDown;
